/**
 * MAGCORE implementation of NFS network plugin.
 *
 * There are different realizations of core NFS API -- old synchronous API and new asynchronous,
 * with callback-based asynchronous model. Plugin realization supports two variations for backward compatibility.
 *
 * @author Yaroslav Surilov <y.surilov@infomir.com>
 */

'use strict';

var NFS_PATH      = '/ram/mnt/nfs', // default dir for NFS mount point
    NFS_MOUNTED   = false,          // flag is a NFS share mounted to the `NFS_PATH`
    apiVersion    = +gSTB.Version().split(';')[0].split(':')[1].trim(),
    asyncApiAvail = window.stbNfs && apiVersion >= 342,
    /**
     * @namespace
     * @property {boolean} enabled enable/disable cache entirely
     * @property {string} activeServer currently opened server
     * @property {Object} data all cached resources: servers and shares
     * @property {number} serversCacheTime value in seconds of lifetime of NFS servers cache
     * @property {number} sharesCacheTime value in seconds of lifetime of NFS shares cache
     * @property {function} setup
     */
    cache = {
        enabled: true,
        workers: [],
        activeServer: null,
        /**
         * @example
         * {
         *     servers: [server_0, server_1, ...],
         *     shares: {
         *         server_0: [share_0, share_1, ...],
         *         server_1: [share_0, share_1, ...]
         *     }
         * }
         */
        data: {
            servers: null,
            shares: null
        },
        serversCacheTime: 300,
        sharesCacheTime: 300,
        setup: function ( worker ) {
            if ( this.workers.indexOf(worker) === -1 ) {
                this.workers.push(worker);
            }

            worker.setupTime = new Date();
        },
        clear: function () {
            cache.data.servers = null;
            cache.data.shares = null;
            cache.activeServer = null;

            cache.workers.forEach(function ( worker ) {
                delete worker.setupTime;
            });

            cache.workers = [];
        }
    };


/**
 * Mount if no connection with the given parameters
 *
 * @param {Object} config opener configuration
 * @param {function} callback scan result handler
 */
function mount ( config, callback ) {
    var status, command;

    if ( NFS_MOUNTED ) {
        status = true;
    } else if ( config && config.address ) {
        config.address = config.address.charAt(config.address.length - 1) === '/' ?
            config.address.substr(0, config.address.length - 1) :
            config.address;
        command = 'mount nfs "' + config.address + '" "' + NFS_PATH + '" nolock,ro,rsize=4096,wsize=4096,udp';
        status = gSTB.RDir(command);

        // global flag
        NFS_MOUNTED = status === 'Ok';
        status = NFS_MOUNTED;
    } else {
        status = false;
    }

    setTimeout(function () {
        callback(null, status);
    }, 0);
}


/**
 * Check mount status and disconnect.
 *
 * @param {boolean} [force=false] flag to forcefully unmount the resource
 */
function umount ( force ) {
    force = arguments.length ? force : false;

    if ( NFS_MOUNTED || force === true ) {
        // unmount the previous share
        gSTB.ExecAction('umount_dir ' + NFS_PATH);
        // global flag
        NFS_MOUNTED = false;
        //NFS_DATA = null;
    }
}


/**
 * Start scanning to find all available servers.
 *
 * @param {function} callback scan result handler
 * @param {boolean} useCache enable/disable caching for this worker
 */
function getServers ( callback, useCache ) {
    var sort = function ( data ) {
        return data.sort(function ( arg1, arg2 ) {
            return arg1.split('.').join('') - arg2.split('.').join('');
        });
    };

    if ( typeof callback !== 'function' ) {
        throw new Error('[NFS plugin] > callback for getServers must be a function');
    }

    useCache = typeof useCache === typeof true ? useCache : true;

    if ( cache.enabled && useCache ) {
        if ( !cache.data.servers ) {
            cache.setup(getServers);
        } else if ( (new Date() - getServers.setupTime) / 1000 > cache.serversCacheTime ) {
            cache.setup(getServers);
            // reset data
            cache.data.servers = null;
            cache.activeServer = null;
        } else {
            callback(null, sort(cache.data.servers));

            return;
        }
    }

    if ( asyncApiAvail ) {
        window.stbNfs.findServers(function ( error, servers ) {
            if ( error ) {
                callback(error);
            } else if ( Array.isArray(servers) ) {
                callback(null, sort(cache.data.servers = servers));

                if ( cache.data.shares ) {
                    Object.keys(cache.data.shares).forEach(function ( key ) {
                        if ( servers.indexOf(key) === -1 ) {
                            delete cache.data.shares[key];
                        }
                    });
                }
            } else {
                callback({message: 'Invalid data format!'});
            }
        });
    }
}


/**
 * Start scanning to find all available exports on the given server.
 *
 * @param {Object} config scan configuration
 * @param {function} callback scan result handler
 * @param {boolean} useCache enable/disable caching for this worker
 */
function getShares ( config, callback, useCache ) {
    if ( typeof callback !== 'function' ) {
        throw new Error('[NFS plugin] > callback for getShares must be a function');
    }

    useCache = typeof useCache === typeof true ? useCache : true;
    NFS_MOUNTED = false;

    if ( cache.enabled && useCache ) {
        cache.activeServer = config.server;

        if ( !cache.data.shares ) {
            cache.setup(getShares);
        } else if ( (new Date() - getShares.setupTime) / 1000 > cache.sharesCacheTime ) {
            cache.setup(getShares);
            // reset data
            cache.data.shares = null;
            cache.activeServer = null;
        } else if ( cache.data.shares[cache.activeServer] ) {
            callback(null, cache.data.shares[cache.activeServer]);

            return;
        }
    }

    if ( asyncApiAvail ) {
        window.stbNfs.findShares(config, function ( error, shares ) {
            if ( error ) {
                callback(error);
            } else if ( Array.isArray(shares) ) {
                cache.data.shares = cache.data.shares || {};
                cache.data.shares[cache.activeServer] = shares;
                callback(null, shares);
            } else {
                callback({message: 'Invalid data format!'});
            }
        });
    }
}


module.exports = {
    onAppInit: function ( config, callback ) {
        var api = {
            config: config.app,
            types: {
                /*   SAMBA_GROUP: 0,
                SAMBA_HOST: 1,
                SAMBA_SHARE: 2*/
            },
            hasNfsNativeSupport: asyncApiAvail,
            clearCache: cache.clear,
            getServers: getServers,
            getShares: getShares,
            mount: mount,
            umount: umount
        };

        callback(null, api);
    }
};
